import axios from "axios";
import { BASE_URL } from "../utils/constants";
import { callProtectedAPI, getAccessToken } from "./authServices";
import { formatApiError } from "../utils/helperUtils";
import { compressAndRenameFile } from "../utils/helperUtils";

/**
 * Generic Axios API Service
 * @param {string} endpoint - API endpoint (relative to BASE_URL)
 * @param {string} errorMessage - Custom error message for failures
 * @param {string} method - HTTP method (GET, POST, PATCH, PUT, DELETE)
 * @param {object|null} data - Request body (optional)
 * @param {object} headers - Additional headers (optional)
 */
const axiosRequest = async (
    endpoint,
    errorMessage,
    method = "GET",
    data = null,
    headers = { "Content-Type": "application/json" }
) => {
    const url = `${BASE_URL}/api/v1${endpoint}`;

    try {
        const response = await axios({
            method,
            url,
            data,
            headers: {
                ...headers,
            },
        });

        return { success: true, data: response.data };
    } catch (error) {
        console.error(`Error calling ${url}:`, error);

        throw new Error(formatApiError(error, errorMessage));
    }
};

export const UpdateEventListingAuth = async (eventSlug, eventData, login, logout, errorMsg = "Update Event Error") => {
    try {
        const { banner_photo, ...restOfData } = eventData;

        // Step 1: PATCH event without banner_photo
        let response;

        if (Object.keys(restOfData).length > 0) {
            response = await callProtectedAPI(
                `/events/listings/${eventSlug}/`,
                "PATCH",
                restOfData,
                {},
                login,
                logout,
                errorMsg
            );

            if (!response.success) {
                return response;
            }
        }

        // Step 2: If banner_photo exists, PATCH it separately

        let patchResponse;

        if (banner_photo) {
            const formData = new FormData();
            const compressedBannerPhoto = await compressAndRenameFile(banner_photo, "event_banner_photo");
            formData.append("banner_photo", compressedBannerPhoto);

            patchResponse = await callProtectedAPI(
                `/events/listings/${eventSlug}/`,
                "PATCH",
                formData,
                { "Content-Type": "multipart/form-data" },
                login,
                logout,
                errorMsg
            );

            if (!patchResponse.success) {
                return patchResponse;
            }
        }

        return response || patchResponse;

    } catch (error) {
        console.error("Error updating event:", error);
        return { success: false, error: error.message };
    }
};

export const NewEventListingAuth = async (eventData, login, logout, errorMsg = "Submission Error") => {
    try {
        const { banner_photo, ...restOfData } = eventData; // remove banner_photo

        // Step 1: POST event without banner_photo
        const response = await callProtectedAPI(
            `/events/listings/`,
            "POST",
            restOfData,
            {},
            login,
            logout,
            errorMsg
        );

        if (!response.success) {
            return response;
        }


        // Step 2: If banner_photo exists, PATCH it separately
        if (banner_photo) {
            const slug = response.data?.slug

            if (slug) {

                const patchResponse = await UpdateEventListingAuth(slug, { banner_photo: await compressAndRenameFile(banner_photo, "event_banner_photo") }, login, logout);

                if (!patchResponse.success) {
                    return patchResponse; // if PATCH fails, return error
                }
            } else {
                return { success: false, error: "Event slug/id missing after creation." };
            }
        }

        return response; // Return original POST response

    } catch (error) {
        console.error("Error creating event:", error);
        return { success: false, error: error.message };
    }
};

export const ApplyEventListingAuth = async (eventSlug, eventData, login, logout, errorMsg = "Apply Event Error") => {
    return callProtectedAPI(
        `/events/listings/${eventSlug}/applications/`,
        "POST",
        eventData,
        {},
        login,
        logout,
        errorMsg
    );
};

export const fetchUserEventListingsAuth = async (login, logout) => {
    return callProtectedAPI(
        `/events/my_listings/`,
        "GET",
        null,
        {},
        login,
        logout
    );
};

export const fetchUserAppliedEventsAuth = async (login, logout) => {
    return callProtectedAPI(
        `/events/my_applications/`,
        "GET",
        null,
        {},
        login,
        logout
    );
};


let debounceTimeout;

export const fetchAddressSuggestionsAuth = (query, login, logout, delay = 1000) => {
    return new Promise((resolve) => {
        if (debounceTimeout) clearTimeout(debounceTimeout);
        debounceTimeout = setTimeout(async () => {
            const response = await callProtectedAPI(
                `/events/address_suggestions/?q=${encodeURIComponent(query)}`,
                "GET",
                null,
                {},
                login,
                logout
            );
            resolve(response);
        }, delay);
    });
};

export const DeleteEventListingAuth = async (eventSlug, login, logout, errorMsg = "Error Deleting Event") => {
    return callProtectedAPI(
        `/events/listings/${eventSlug}/`,
        "DELETE",
        null,
        {},
        login,
        logout,
        errorMsg
    );
};

export const WithdrawEventApplicationAuth = async (eventSlug, vendorSlug, login, logout, errorMsg = "Error Deleting Event Application") => {
    return callProtectedAPI(
        `/events/listings/${eventSlug}/applications/${vendorSlug}/`,
        "DELETE",
        null,
        {},
        login,
        logout,
        errorMsg
    );
};

export const fetchPublicEventDetail = async (slug, login, logout) => {
    const token = getAccessToken();

    if (token) {
        return callProtectedAPI(
            `/events/listings/${slug}/`,
            "GET",
            null,
            {},
            login,
            logout,
            "Failed to fetch event details."
        );
    }

    return axiosRequest(
        `/events/listings/${slug}/`,
        "Failed to fetch event details.",
        "GET"
    );
};

export const fetchEventApplicationsOrganizerAuth = async (eventSlug, login, logout) => {
    return callProtectedAPI(
        `/events/listings/${eventSlug}/applications/`,
        "GET",
        null,
        {},
        login,
        logout
    );
};

export const ApproveEventListingAuth = async (eventSlug, vendorSlug, applicationStatus, comment = "", login, logout, errorMsg = "Approve Event Error") => {

    const body = {
        application_status: applicationStatus,
        ...(comment && { host_comment: comment })
    };

    return callProtectedAPI(
        `/events/listings/${eventSlug}/applications/${vendorSlug}/status/`,
        "PATCH",
        body,
        {},
        login,
        logout,
        errorMsg
    );
};

export const fetchEventListingsNearby = async ({ miles, lng, lat }, errorMsg = "Failed to fetch public events nearby") => {
    const query = new URLSearchParams({ miles, lng, lat }).toString();

    return axiosRequest(
        `/events/listings/?${query}`,
        errorMsg,
        "GET"
    );
};

export const TrackUserApplyExternalAuth = async (eventSlug, login, logout) => {
    try {
        await callProtectedAPI(
            `/events/listings/${eventSlug}/cash-applications/`,
            "POST",
            {},
            {},
            login,
            logout
        );
    } catch (error) {
        console.warn("Tracking external application failed:", error?.message || error);
        // Ignore the error
    }
};